import { useCallback, useEffect, useMemo, useState } from 'react';
import { apiFetch, apiFetchBlob } from '../config/api';

const DEFAULT_STATEMENT = 'I give permission for my child, {child_name}, to participate in {trip_title} at {destination}. I have reviewed the departure and return times and the instructions for this trip. This permission is only for {child_name} and this trip.';

function newId(prefix) {
  return `${prefix}_${Math.random().toString(36).slice(2, 10)}`;
}

function retryKey() {
  return `ft_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
}

function emptyDraft() {
  return {
    title: '',
    description: '',
    destination: '',
    departs_at_local: '',
    returns_at_local: '',
    expires_at_local: '',
    instructions: '',
    school_contact: '',
    permission_statement: DEFAULT_STATEMENT,
    questions: [],
    class_ids: [],
    grade_ids: [],
    teacher_ids: [],
    student_ids: [],
    excluded_student_ids: [],
  };
}

function growField(event) {
  const field = event.target;
  field.style.height = 'auto';
  field.style.height = `${field.scrollHeight}px`;
}

function previewWhen(value) {
  const match = String(value || '').match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/);
  if (!match) return '—';
  const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]), Number(match[4]), Number(match[5]));
  return date.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

function previewStatement(template, draft) {
  return String(template || '').replace(/\{(child_name|trip_title|destination)\}/g, (_, key) => {
    if (key === 'child_name') return 'your child';
    if (key === 'trip_title') return draft.title || 'this trip';
    return draft.destination || 'the address';
  });
}

function publishProblems(draft, selectedCount) {
  const missing = [];
  if (!String(draft.title || '').trim()) missing.push('Trip title');
  if (!String(draft.destination || '').trim()) missing.push('Address');
  if (!draft.departs_at_local) missing.push('Departure');
  if (!draft.returns_at_local) missing.push('Return');
  if (!draft.expires_at_local) missing.push('Form expires');
  if (!String(draft.permission_statement || '').trim()) missing.push('Permission statement');
  if (!(draft.grade_ids || []).length && !(draft.teacher_ids || []).length) missing.push('At least one grade or teacher');
  else if (!selectedCount) missing.push('The selected grades and teachers do not include any students');
  (draft.questions || []).forEach((question, index) => {
    const number = index + 1;
    if (!String(question.label || '').trim()) missing.push(`Question ${number} label`);
    if ((question.type === 'single_choice' || question.type === 'multi_choice') && (question.options || []).filter((option) => String(option.label || '').trim()).length < 2) {
      missing.push(`Question ${number} needs at least two choices`);
    }
  });
  if (draft.departs_at_local && draft.returns_at_local && draft.returns_at_local <= draft.departs_at_local) {
    missing.push('Return time has to be after departure');
  }
  if (draft.expires_at_local && new Date(draft.expires_at_local).getTime() <= Date.now()) {
    missing.push('Expiration has to be in the future');
  }
  return missing;
}

function statusLabel(status) {
  if (status === 'published') return 'Published';
  if (status === 'expired') return 'Expired';
  if (status === 'closed') return 'Closed';
  if (status === 'approved') return 'Approved';
  if (status === 'declined') return 'Declined';
  if (status === 'awaiting') return 'Awaiting';
  return 'Draft';
}

export default function FieldTripFormsPage({ token, schoolId }) {
  const [view, setView] = useState('list');
  const [forms, setForms] = useState([]);
  const [query, setQuery] = useState('');
  const [status, setStatus] = useState('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [bundle, setBundle] = useState(null);
  const [draft, setDraft] = useState(emptyDraft());
  const [directory, setDirectory] = useState({ grades: [], teachers: [], students: [] });
  const [saving, setSaving] = useState(false);
  const [publishOpen, setPublishOpen] = useState(false);
  const [preview, setPreview] = useState(null);
  const [publishKey, setPublishKey] = useState('');
  const [roster, setRoster] = useState({ form: null, rows: [] });
  const [rosterQuery, setRosterQuery] = useState('');
  const [rosterStatus, setRosterStatus] = useState('all');
  const [response, setResponse] = useState(null);
  const [signatureUrl, setSignatureUrl] = useState('');
  const [remindOpen, setRemindOpen] = useState(false);
  const [remindPlan, setRemindPlan] = useState(null);
  const [remindKey, setRemindKey] = useState('');
  const [closeOpen, setCloseOpen] = useState(false);
  const [publishMissing, setPublishMissing] = useState([]);

  useEffect(() => {
    if (!response) return undefined;
    const close = () => setResponse(null);
    const onKey = (event) => {
      if (event.key === 'Escape') close();
    };
    const onSlide = (event) => {
      const panel = event.target instanceof Element ? event.target.closest('.ft-detail') : null;
      if (panel) return;
      close();
    };
    window.addEventListener('keydown', onKey);
    window.addEventListener('wheel', onSlide, { passive: true });
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('wheel', onSlide);
    };
  }, [response]);

  const loadList = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams({ school_id: String(schoolId), q: query, status });
      const result = await apiFetch(`/field-trips?${params}`, {}, token);
      setForms(result.data || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [query, schoolId, status, token]);

  useEffect(() => {
    if (view === 'list') loadList();
  }, [loadList, view]);

  const loadDirectory = useCallback(async () => {
    const result = await apiFetch(`/field-trips/classes?school_id=${schoolId}`, {}, token);
    setDirectory(result.data || { grades: [], teachers: [], students: [] });
  }, [schoolId, token]);

  async function openForm(formId, nextView = 'editor') {
    setError('');
    setNotice('');
    const result = await apiFetch(`/field-trips/${formId}?school_id=${schoolId}`, {}, token);
    setBundle(result.data);
    setDraft(result.data.draft || emptyDraft());
    setView(nextView);
    if (nextView === 'editor') await loadDirectory();
    if (nextView === 'roster') await loadRoster(formId, '', 'all');
  }

  async function loadRoster(formId, q, nextStatus) {
    const params = new URLSearchParams({ school_id: String(schoolId), q, status: nextStatus });
    const result = await apiFetch(`/field-trips/${formId}/roster?${params}`, {}, token);
    setRoster(result.data);
  }

  function updateDraft(patch) {
    setDraft((current) => ({ ...current, ...patch }));
  }

  function updateQuestion(id, patch) {
    setDraft((current) => ({
      ...current,
      questions: current.questions.map((question) => (question.id === id ? { ...question, ...patch } : question)),
    }));
  }

  function moveQuestion(id, direction) {
    setDraft((current) => {
      const questions = [...current.questions];
      const index = questions.findIndex((question) => question.id === id);
      const target = index + direction;
      if (index < 0 || target < 0 || target >= questions.length) return current;
      const [item] = questions.splice(index, 1);
      questions.splice(target, 0, item);
      return { ...current, questions };
    });
  }

  async function saveDraft(nextDraft = draft) {
    const payload = { ...nextDraft, class_ids: [], student_ids: [] };
    setSaving(true);
    setError('');
    try {
      if (!bundle?.form?.id) {
        const created = await apiFetch('/field-trips', {
          method: 'POST',
          body: JSON.stringify({ school_id: schoolId, ...payload }),
        }, token);
        setBundle(created.data);
        setDraft(created.data.draft);
        setNotice('Draft saved.');
        return created.data;
      }
      const saved = await apiFetch(`/field-trips/${bundle.form.id}`, {
        method: 'PUT',
        body: JSON.stringify({ school_id: schoolId, ...payload }),
      }, token);
      setBundle(saved.data);
      setDraft(saved.data.draft);
      setNotice(saved.data.form.status === 'draft' ? 'Draft saved.' : 'Changes saved as a draft. The published form is unchanged until you publish a replacement.');
      return saved.data;
    } catch (err) {
      setError(err.message);
      return null;
    } finally {
      setSaving(false);
    }
  }

  async function startPublish() {
    const missing = publishProblems(draft, selectedStudents.length);
    if (missing.length) {
      setPublishMissing(missing);
      setNotice('');
      setPublishOpen(false);
      return;
    }
    setPublishMissing([]);
    const saved = await saveDraft();
    if (!saved) return;
    setPublishKey(retryKey());
    setPublishOpen(true);
    try {
      const result = await apiFetch('/field-trips/recipient-preview', {
        method: 'POST',
        body: JSON.stringify({ school_id: schoolId, ...saved.draft }),
      }, token);
      setPreview(result.data);
    } catch (err) {
      setError(err.message);
    }
  }

  async function excludeBlocked() {
    const blockedIds = (preview?.blocked || []).map((student) => student.id);
    const next = {
      ...draft,
      excluded_student_ids: [...new Set([...(draft.excluded_student_ids || []), ...blockedIds])],
    };
    setDraft(next);
    const saved = await saveDraft(next);
    if (!saved) return;
    const result = await apiFetch('/field-trips/recipient-preview', {
      method: 'POST',
      body: JSON.stringify({ school_id: schoolId, ...saved.draft }),
    }, token);
    setPreview(result.data);
  }

  async function confirmPublish() {
    if (!bundle?.form?.id || !publishKey) return;
    setSaving(true);
    setError('');
    try {
      const result = await apiFetch(`/field-trips/${bundle.form.id}/publish`, {
        method: 'POST',
        body: JSON.stringify({
          school_id: schoolId,
          idempotency_key: publishKey,
          replace: bundle.form.status !== 'draft',
        }),
      }, token);
      setBundle(result.data);
      setDraft(result.data.draft);
      setPublishOpen(false);
      setNotice(result.data.replayed ? 'This publish was already completed.' : 'The form is published and families were notified.');
      setView('roster');
      await loadRoster(bundle.form.id, '', 'all');
    } catch (err) {
      setError(err.message);
      if (err.data?.students) setPreview((current) => ({ ...(current || {}), blocked: err.data.students }));
    } finally {
      setSaving(false);
    }
  }

  async function duplicate() {
    if (!bundle?.form?.id) return;
    const result = await apiFetch(`/field-trips/${bundle.form.id}/duplicate`, {
      method: 'POST',
      body: JSON.stringify({ school_id: schoolId }),
    }, token);
    setNotice('Copied into a new draft. Responses were not copied.');
    await openForm(result.data.form.id, 'editor');
  }

  async function confirmClose() {
    setSaving(true);
    setError('');
    try {
      const result = await apiFetch(`/field-trips/${bundle.form.id}/close`, {
        method: 'POST',
        body: JSON.stringify({ school_id: schoolId }),
      }, token);
      setBundle(result.data);
      setCloseOpen(false);
      setNotice('Submissions are closed. Parents can no longer respond, and the responses already on file stay available.');
      await loadRoster(bundle.form.id, rosterQuery, rosterStatus);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  async function exportCsv() {
    const blob = await apiFetchBlob(`/field-trips/${bundle.form.id}/export?school_id=${schoolId}`, {}, token);
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${draft.title || 'field-trip'}-responses.csv`;
    link.click();
    URL.revokeObjectURL(url);
  }

  async function openRemind() {
    setRemindKey(retryKey());
    const result = await apiFetch(`/field-trips/${bundle.form.id}/reminders/preview`, {
      method: 'POST',
      body: JSON.stringify({ school_id: schoolId }),
    }, token);
    setRemindPlan(result.data);
    setRemindOpen(true);
  }

  async function sendRemind() {
    setSaving(true);
    setError('');
    try {
      const result = await apiFetch(`/field-trips/${bundle.form.id}/reminders`, {
        method: 'POST',
        body: JSON.stringify({ school_id: schoolId, idempotency_key: remindKey }),
      }, token);
      setRemindOpen(false);
      setNotice(result.data.replayed
        ? 'This reminder was already sent.'
        : `Reminder sent for ${result.data.unanswered_students} students to ${result.data.guardian_count} guardians.`);
      const refreshed = await apiFetch(`/field-trips/${bundle.form.id}?school_id=${schoolId}`, {}, token);
      setBundle(refreshed.data);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  async function openResponse(row) {
    const result = await apiFetch(`/field-trips/${bundle.form.id}/responses/${row.response_id}?school_id=${schoolId}`, {}, token);
    setResponse(result.data);
    setSignatureUrl('');
    if (result.data.has_signature) {
      const blob = await apiFetchBlob(`/field-trips/responses/${result.data.id}/signature?school_id=${schoolId}`, {}, token);
      setSignatureUrl(URL.createObjectURL(blob));
    }
  }

  const selectedStudents = useMemo(() => {
    const gradeIds = new Set(draft.grade_ids || []);
    const teacherIds = new Set((draft.teacher_ids || []).map((id) => Number(id)));
    const excluded = new Set(draft.excluded_student_ids || []);
    const byId = new Map();
    (directory.students || []).forEach((student) => {
      if (excluded.has(student.id)) return;
      const coveredByTeacher = (student.teacher_ids || []).some((id) => teacherIds.has(Number(id)));
      if (gradeIds.has(student.grade_id) || coveredByTeacher) {
        byId.set(student.id, student);
      }
    });
    return [...byId.values()];
  }, [directory.students, draft.excluded_student_ids, draft.grade_ids, draft.teacher_ids]);

  function toggleId(field, value) {
    const normalize = field === 'teacher_ids' ? (item) => Number(item) : (item) => item;
    const current = new Set((draft[field] || []).map(normalize));
    const next = normalize(value);
    if (current.has(next)) current.delete(next);
    else current.add(next);
    updateDraft({ [field]: [...current] });
  }

  return (
    <div className="ft-page">
      <div className="ft-heading">
        <div>
          <h1>Field Trip Forms</h1>
          <p>Create a permission form, publish it to parents, and track each student’s response.</p>
        </div>
        {view === 'list' ? (
          <button type="button" className="sa-btn sa-btn-primary" onClick={() => { setBundle(null); setDraft(emptyDraft()); setView('editor'); loadDirectory(); }}>
            New form
          </button>
        ) : (
          <button type="button" className="sa-btn" onClick={() => { setView('list'); setResponse(null); }}>
            All forms
          </button>
        )}
      </div>

      {error ? <div className="ft-alert" role="alert">{error}</div> : null}
      {publishMissing.length ? (
        <div className="ft-alert" role="alert">
          <p>Add the missing information before publishing.</p>
          <ul>{publishMissing.map((item) => <li key={item}>{item}</li>)}</ul>
        </div>
      ) : null}
      {notice ? <div className="ft-notice" role="status">{notice}</div> : null}

      {view === 'list' ? (
        <section className="ft-panel">
          <div className="ft-toolbar">
            <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search by title" aria-label="Search forms" />
            <select value={status} onChange={(event) => setStatus(event.target.value)} aria-label="Filter by status">
              <option value="all">All statuses</option>
              <option value="draft">Draft</option>
              <option value="published">Published</option>
              <option value="expired">Expired</option>
              <option value="closed">Closed</option>
            </select>
          </div>
          {loading ? <p>Loading forms…</p> : null}
          {!loading && forms.length === 0 ? <p>No field trip forms yet.</p> : null}
          {forms.length > 0 ? (
            <table className="ft-table">
              <thead>
                <tr>
                  <th>Title</th>
                  <th>Trip date</th>
                  <th>Expiration</th>
                  <th>Status</th>
                  <th>Students</th>
                  <th>Approved</th>
                  <th>Declined</th>
                  <th>Awaiting</th>
                </tr>
              </thead>
              <tbody>
                {forms.map((form) => (
                  <tr key={form.id} onClick={() => openForm(form.id, form.lifecycle_status === 'draft' ? 'editor' : 'roster')}>
                    <td>{form.title}</td>
                    <td>{form.trip_date_label || '—'}</td>
                    <td>{form.expires_at_label || '—'}</td>
                    <td><span className={`ft-status ft-${form.lifecycle_status}`}>{statusLabel(form.lifecycle_status)}</span></td>
                    <td>{form.student_count}</td>
                    <td>{form.approved_count}</td>
                    <td>{form.declined_count}</td>
                    <td>{form.awaiting_count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : null}
        </section>
      ) : null}

      {view === 'editor' ? (
        <div className="ft-builder">
          <section className="ft-panel">
            <h2>{bundle?.form?.status === 'published' ? 'Edit next version' : 'Form setup'}</h2>
            {bundle?.form?.status === 'published' ? (
              <p className="ft-help">Saving keeps a draft. Publishing a replacement asks every selected family for a new signature. Earlier approvals stay on file and do not approve the new wording.</p>
            ) : null}
            <label>Trip title<input value={draft.title} onChange={(event) => updateDraft({ title: event.target.value })} required /></label>
            <label>Description<textarea value={draft.description} onChange={(event) => updateDraft({ description: event.target.value })} rows={3} /></label>
            <label>Address<input value={draft.destination} onChange={(event) => updateDraft({ destination: event.target.value })} placeholder="Street, city" /></label>
            <div className="ft-grid">
              <label>Departure<input type="datetime-local" value={draft.departs_at_local} onChange={(event) => updateDraft({ departs_at_local: event.target.value })} /></label>
              <label>Return<input type="datetime-local" value={draft.returns_at_local} onChange={(event) => updateDraft({ returns_at_local: event.target.value })} /></label>
              <label>Form expires<input type="datetime-local" value={draft.expires_at_local} onChange={(event) => updateDraft({ expires_at_local: event.target.value })} /></label>
            </div>
            <p className="ft-help">Times use the school timezone{bundle?.time_zone ? ` (${bundle.time_zone})` : ''}.</p>
            <label>What students should bring<textarea value={draft.instructions} onChange={(event) => updateDraft({ instructions: event.target.value })} rows={3} /></label>
            <label>Permission statement<textarea value={draft.permission_statement} onChange={(event) => updateDraft({ permission_statement: event.target.value })} rows={4} /></label>
            <p className="ft-help">Use {'{child_name}'}, {'{trip_title}'}, and {'{destination}'}. {'{destination}'} is the address. Parents see their own child’s name.</p>

            <div className="ft-section-head ft-questions-head">
              <div>
                <h3>Questions</h3>
                <p className="ft-help">Use the arrows to reorder the questions. Parents see them in this order.</p>
              </div>
              <button type="button" className="sa-btn" onClick={() => updateDraft({ questions: [...draft.questions, { id: newId('q'), type: 'short_text', label: '', helper: '', required: false, options: [] }] })}>
                Add question
              </button>
            </div>
            {draft.questions.map((question, index) => (
              <div className="ft-question" key={question.id}>
                <div className="ft-question-head">
                  <label>Label
                    <textarea
                      className="ft-grow"
                      rows={1}
                      value={question.label}
                      ref={(node) => {
                        if (!node) return;
                        node.style.height = 'auto';
                        node.style.height = `${node.scrollHeight}px`;
                      }}
                      onChange={(event) => {
                        growField(event);
                        updateQuestion(question.id, { label: event.target.value });
                      }}
                    />
                  </label>
                  <label>Type
                    <select value={question.type} onChange={(event) => {
                      const type = event.target.value;
                      const options = type === 'yes_no'
                        ? [{ id: 'yes', label: 'Yes' }, { id: 'no', label: 'No' }]
                        : (type === 'single_choice' || type === 'multi_choice' ? (question.options?.length ? question.options : [{ id: newId('o'), label: '' }, { id: newId('o'), label: '' }]) : []);
                      updateQuestion(question.id, { type, options });
                    }}>
                      <option value="short_text">Short text</option>
                      <option value="long_text">Long text</option>
                      <option value="single_choice">Multiple choice</option>
                      <option value="multi_choice">Checkboxes</option>
                      <option value="yes_no">Yes / No</option>
                    </select>
                  </label>
                </div>
                <label>Helper text
                  <textarea
                    className="ft-grow ft-helper"
                    rows={1}
                    value={question.helper || ''}
                    placeholder="Optional note for parents"
                    ref={(node) => {
                      if (!node) return;
                      node.style.height = 'auto';
                      node.style.height = `${node.scrollHeight}px`;
                    }}
                    onChange={(event) => {
                      growField(event);
                      updateQuestion(question.id, { helper: event.target.value });
                    }}
                  />
                </label>
                <label className="ft-check"><input type="checkbox" checked={Boolean(question.required)} onChange={(event) => updateQuestion(question.id, { required: event.target.checked })} /> Required</label>
                {(question.type === 'single_choice' || question.type === 'multi_choice') ? (
                  <div className="ft-options">
                    {(question.options || []).map((option) => (
                      <div key={option.id} className="ft-option-row">
                        <input value={option.label} onChange={(event) => updateQuestion(question.id, { options: question.options.map((item) => item.id === option.id ? { ...item, label: event.target.value } : item) })} placeholder="Choice" />
                        <button type="button" className="ft-text-btn" onClick={() => updateQuestion(question.id, { options: question.options.filter((item) => item.id !== option.id) })}>Remove</button>
                      </div>
                    ))}
                    <button type="button" className="ft-text-btn" onClick={() => updateQuestion(question.id, { options: [...(question.options || []), { id: newId('o'), label: '' }] })}>Add choice</button>
                  </div>
                ) : null}
                <div className="ft-question-actions">
                  <div className="ft-reorder">
                    <button type="button" className="ft-arrow" aria-label="Move question up" onClick={() => moveQuestion(question.id, -1)} disabled={index === 0}>
                      <svg viewBox="0 0 16 16" aria-hidden="true"><path d="M3.5 10.25 8 5.75l4.5 4.5" /></svg>
                    </button>
                    <button type="button" className="ft-arrow" aria-label="Move question down" onClick={() => moveQuestion(question.id, 1)} disabled={index === draft.questions.length - 1}>
                      <svg viewBox="0 0 16 16" aria-hidden="true"><path d="M3.5 5.75 8 10.25l4.5-4.5" /></svg>
                    </button>
                  </div>
                  <button type="button" className="ft-text-btn ft-text-btn-danger" onClick={() => updateDraft({ questions: draft.questions.filter((item) => item.id !== question.id) })}>Remove question</button>
                </div>
              </div>
            ))}

            <h3 className="ft-recipients-title">Recipients</h3>
            <p className="ft-help">Choose one or more grades, one or more teachers, or both. A student is included once.</p>
            <p className="ft-recipient-label">Grades</p>
            <div className="ft-chips">
              {(directory.grades || []).map((grade) => {
                const selected = (draft.grade_ids || []).includes(grade.id);
                return (
                  <button
                    key={grade.id}
                    type="button"
                    className={selected ? 'ft-chip is-on' : 'ft-chip'}
                    aria-pressed={selected}
                    onClick={() => toggleId('grade_ids', grade.id)}
                  >
                    {grade.label}
                    <span>{grade.student_count}</span>
                  </button>
                );
              })}
            </div>
            <p className="ft-recipient-label">Teachers</p>
            {(directory.teachers || []).length ? (
              <div className="ft-chips">
                {directory.teachers.map((teacher) => {
                  const selected = (draft.teacher_ids || []).map(Number).includes(Number(teacher.id));
                  return (
                    <button
                      key={teacher.id}
                      type="button"
                      className={selected ? 'ft-chip is-on' : 'ft-chip'}
                      aria-pressed={selected}
                      title={teacher.detail || undefined}
                      onClick={() => toggleId('teacher_ids', Number(teacher.id))}
                    >
                      {teacher.name}
                      <span>{teacher.student_count}</span>
                    </button>
                  );
                })}
              </div>
            ) : <p className="ft-help">No teachers are assigned to a grade yet.</p>}
            <p className="ft-selected">{selectedStudents.length} students selected</p>

            <div className="ft-actions">
              <button type="button" className="sa-btn" onClick={() => saveDraft()} disabled={saving}>Save draft</button>
              {bundle?.form?.id ? <button type="button" className="sa-btn" onClick={duplicate}>Duplicate</button> : null}
              <button type="button" className="sa-btn sa-btn-primary" onClick={startPublish} disabled={saving}>
                {bundle?.form?.status === 'draft' || !bundle?.form ? 'Review & publish' : 'Publish replacement'}
              </button>
            </div>
          </section>
          <aside className="ft-preview" aria-label="Parent preview">
            <p className="ft-preview-kicker">Parent view</p>
            <div className="ft-phone">
              <p className="ft-phone-title">{draft.title || 'Trip title'}</p>
              <p className="ft-phone-child">For your child</p>
              <p className="ft-phone-meta">{draft.destination || 'Address'}</p>
              <p className="ft-phone-meta">Departs {previewWhen(draft.departs_at_local)}</p>
              <p className="ft-phone-meta">Returns {previewWhen(draft.returns_at_local)}</p>
              <p className="ft-phone-meta">Respond by {previewWhen(draft.expires_at_local)}{bundle?.time_zone ? ` (${bundle.time_zone})` : ''}</p>
              {draft.description?.trim() ? <p className="ft-phone-body">{draft.description}</p> : null}
              {draft.instructions?.trim() ? <p className="ft-phone-body">Bring: {draft.instructions}</p> : null}
              {draft.questions.map((question) => (
                <div key={question.id} className="ft-phone-question">
                  <p className="ft-phone-section">{question.label.trim() || 'Question'}{question.required ? ' *' : ''}</p>
                  {question.helper?.trim() ? <p className="ft-phone-help">{question.helper}</p> : null}
                  {question.type === 'short_text' || question.type === 'long_text' ? (
                    <div className={question.type === 'long_text' ? 'ft-phone-field is-long' : 'ft-phone-field'} />
                  ) : (
                    (question.options || []).map((option) => (
                      <div key={option.id} className="ft-phone-choice">{option.label.trim() || 'Choice'}</div>
                    ))
                  )}
                </div>
              ))}
              <p className="ft-phone-section">Permission</p>
              <p className="ft-phone-body">{previewStatement(draft.permission_statement, draft)}</p>
              <p className="ft-phone-section">Signature</p>
              <div className="ft-phone-pad" aria-hidden="true" />
              <p className="ft-phone-help">Draw with your finger. This records your mark and does not verify your identity.</p>
              <div className="ft-phone-field">Type your name</div>
              <div className="ft-phone-check">
                <span className="ft-phone-box" aria-hidden="true" />
                <span>I confirm that I am an authorized parent or guardian of your child, that I intend to sign this permission electronically, and that this permission is for your child. A drawn signature records my mark. It does not verify my identity or guarantee that the signature is legally enforceable.</span>
              </div>
              <div className="ft-phone-sign">Sign & Approve</div>
              <p className="ft-phone-section">Decline instead</p>
              <div className="ft-phone-field is-long">Optional reason</div>
              <div className="ft-phone-decline">Decline Permission</div>
            </div>
          </aside>
        </div>
      ) : null}

      {view === 'roster' && bundle ? (
        <section className="ft-panel">
          <header className="ft-roster-head">
            <div>
              <h2>{bundle.form.title}</h2>
              <p className="ft-help">{statusLabel(bundle.form.lifecycle_status)} · Expires {bundle.form.expires_at_label || '—'}</p>
            </div>
            <div className="ft-stats" aria-label="Response totals">
              <div><strong>{roster.form?.student_count ?? bundle.form.student_count}</strong><span>Students</span></div>
              <div><strong>{roster.form?.approved_count ?? bundle.form.approved_count}</strong><span>Approved</span></div>
              <div><strong>{roster.form?.declined_count ?? bundle.form.declined_count}</strong><span>Declined</span></div>
              <div><strong>{roster.form?.awaiting_count ?? bundle.form.awaiting_count}</strong><span>Awaiting</span></div>
            </div>
          </header>
          <div className="ft-roster-bar">
            <button type="button" className="sa-btn" onClick={() => openForm(bundle.form.id, 'editor')}>Edit draft</button>
            <button type="button" className="sa-btn" onClick={exportCsv}>Export CSV</button>
            {bundle.form.lifecycle_status === 'published' ? <button type="button" className="sa-btn" onClick={openRemind}>Remind awaiting parents</button> : null}
            {bundle.form.status === 'published' ? <button type="button" className="sa-btn" onClick={() => { setError(''); setCloseOpen(true); }}>Close submissions</button> : null}
            <input className="ft-roster-search" value={rosterQuery} onChange={(event) => { setRosterQuery(event.target.value); loadRoster(bundle.form.id, event.target.value, rosterStatus); }} placeholder="Search students" aria-label="Search roster" />
            <select value={rosterStatus} onChange={(event) => { setRosterStatus(event.target.value); loadRoster(bundle.form.id, rosterQuery, event.target.value); }} aria-label="Filter responses">
              <option value="all">All</option>
              <option value="awaiting">Awaiting</option>
              <option value="approved">Approved</option>
              <option value="declined">Declined</option>
            </select>
          </div>
          <table className="ft-table">
            <thead>
              <tr>
                <th>Student</th>
                <th>Class</th>
                <th>Status</th>
                <th>Guardian</th>
                <th>Submitted</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {roster.rows.map((row) => (
                <tr key={row.request_id}>
                  <td>{row.student_name}</td>
                  <td>{row.class_label}</td>
                  <td>{statusLabel(row.status)}</td>
                  <td>{row.guardian_name || '—'}</td>
                  <td>{row.submitted_at_label || '—'}</td>
                  <td>{row.response_id ? <button type="button" className="ft-view" onClick={() => openResponse(row)}>View response</button> : null}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {bundle.history?.length ? (
            <div>
              <h3>Earlier signed versions</h3>
              <ul>
                {bundle.history.map((item) => (
                  <li key={item.id}>{item.decision} · version {item.version_id} · {item.submitted_at_label}</li>
                ))}
              </ul>
            </div>
          ) : null}
          {bundle.reminders?.length ? (
            <div>
              <h3>Reminder history</h3>
              <ul>
                {bundle.reminders.map((item) => (
                  <li key={item.id}>{item.unanswered_students} students · {item.guardian_count} guardians</li>
                ))}
              </ul>
            </div>
          ) : null}
        </section>
      ) : null}

      {response ? (
        <aside className="ft-detail" role="dialog" aria-label={response.decision === 'approved' ? 'Approved response' : 'Declined response'}>
          <div className="ft-detail-head">
            <div>
              <p className="ft-detail-kicker">{response.decision === 'approved' ? 'Approved' : 'Declined'}</p>
              <h2>{response.student?.name}</h2>
              {response.student?.class_label ? <p className="ft-detail-meta">{response.student.class_label}</p> : null}
            </div>
            <button type="button" className="ft-detail-close" onClick={() => setResponse(null)} aria-label="Close">×</button>
          </div>
          <div className="ft-detail-body">
            <dl className="ft-detail-facts">
              <div><dt>Guardian</dt><dd>{response.guardian?.name || '—'}</dd></div>
              {response.signer_name ? <div><dt>Signed by</dt><dd>{response.signer_name}</dd></div> : null}
              <div><dt>Submitted</dt><dd>{response.submitted_at_label || '—'}</dd></div>
            </dl>
            {response.answers.map((answer) => (
              <p key={answer.question_id} className="ft-detail-copy"><strong>{answer.label}:</strong> {answer.answer || '—'}</p>
            ))}
            {response.decline_reason ? <p className="ft-detail-copy">Reason: {response.decline_reason}</p> : null}
            {response.permission_statement ? <p className="ft-detail-copy">{response.permission_statement}</p> : null}
            {response.consent_acknowledgment ? <p className="ft-detail-note">{response.consent_acknowledgment}</p> : null}
            {signatureUrl ? <img src={signatureUrl} alt="Submitted signature" className="ft-signature" /> : null}
            <p className="ft-detail-note">The signature records the mark that was drawn. It does not verify identity.</p>
          </div>
        </aside>
      ) : null}

      {publishOpen ? (
        <div className="ft-modal" role="dialog" aria-modal="true">
          <div className="ft-modal-card">
            <h2>{bundle?.form?.status === 'draft' ? 'Publish this form?' : 'Publish a replacement?'}</h2>
            {bundle?.form?.status !== 'draft' ? (
              <p>Families will be asked to sign the new wording. Previous approvals remain stored and will not count for this version.</p>
            ) : null}
            <p className="ft-modal-lead">{preview ? `${preview.student_count} students · ${preview.guardian_count} guardians` : 'Checking recipients…'}</p>
            <p>Only the grades and teachers you selected receive this form.</p>
            {preview?.blocked?.length ? (
              <div className="ft-modal-blocked">
                <p>These students have no guardian who can sign:</p>
                <ul className="ft-modal-list">{preview.blocked.map((student) => <li key={student.id}>{student.name}</li>)}</ul>
                <button type="button" className="sa-btn" onClick={excludeBlocked}>Exclude them and continue</button>
              </div>
            ) : null}
            <div className="ft-actions">
              <button type="button" className="sa-btn" onClick={() => setPublishOpen(false)}>Cancel</button>
              <button type="button" className="sa-btn sa-btn-primary" onClick={confirmPublish} disabled={saving || !preview || preview.blocked?.length || !preview.student_count}>
                Publish
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {closeOpen ? (
        <div className="ft-modal" role="dialog" aria-modal="true" aria-labelledby="ft-close-title">
          <div className="ft-modal-card">
            <h2 id="ft-close-title">Close submissions?</h2>
            <p>Parents will no longer be able to approve or decline this trip. Responses already submitted stay on file, and you can still export them.</p>
            {error ? <div className="ft-alert" role="alert">{error}</div> : null}
            <div className="ft-actions">
              <button type="button" className="sa-btn" onClick={() => { setCloseOpen(false); setError(''); }} disabled={saving}>Cancel</button>
              <button type="button" className="sa-btn sa-btn-danger" onClick={confirmClose} disabled={saving}>Close submissions</button>
            </div>
          </div>
        </div>
      ) : null}

      {remindOpen ? (
        <div className="ft-modal" role="dialog" aria-modal="true">
          <div className="ft-modal-card">
            <h2>Remind awaiting parents</h2>
            <p>{remindPlan ? `${remindPlan.unanswered_students} students still need a response, across ${remindPlan.guardian_count} guardians.` : 'Checking…'}</p>
            {remindPlan?.cooldown_until ? <p>A reminder was sent recently. You can send another after the cooldown.</p> : null}
            <div className="ft-actions">
              <button type="button" className="sa-btn" onClick={() => setRemindOpen(false)}>Cancel</button>
              <button type="button" className="sa-btn sa-btn-primary" onClick={sendRemind} disabled={saving || !remindPlan || remindPlan.cooldown_until}>
                Send reminder
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
